// ============================================
// Memory Edit - Quote Layer (ES6)
// 대사 레이어 UI
// ============================================

import { escapeHtml } from '../../utils.js';
import { renderEmptyState, renderLayerHeader, LAYER_CONFIG } from '../accordion.js';
import { IMPORTANCE_CONFIG } from './event.js';
import { getDisplayedCount } from '../state.js';

// ============================================
// Quote 레이어 렌더링 (페이지네이션 적용)
// ============================================

export function renderQuoteLayer(data) {
  if (!data || data.length === 0) {
    return renderEmptyState('quote');
  }

  // API에서 이미 최신순 (createdAt desc)으로 정렬됨
  const sortedData = data;
  const displayCount = getDisplayedCount('quote');
  const displayedItems = sortedData.slice(0, displayCount);
  const hasMore = sortedData.length > displayCount;
  const remainingCount = sortedData.length - displayCount;

  return `
    ${renderLayerHeader('quote')}
    <div class="memory-quote-list">
      ${displayedItems.map(quote => renderQuoteItem(quote)).join('')}
    </div>
    ${hasMore ? `
      <button class="memory-load-more-btn" data-action="loadMore" data-layer="quote">
        더보기 (${remainingCount}개 더)
      </button>
    ` : ''}
  `;
}

// ============================================
// Quote 아이템 렌더링
// ============================================

function renderQuoteItem(quote) {
  const importance = IMPORTANCE_CONFIG[quote.importance] || IMPORTANCE_CONFIG.medium;
  const context = quote.context || {};

  return `
    <div class="memory-layer-item quote-item" data-id="${quote.id}" data-layer="quote">
      <div class="quote-content">
        <span class="quote-mark">"</span>
        <span class="quote-text">${escapeHtml(quote.text || '')}</span>
        <span class="quote-mark">"</span>
      </div>

      <div class="quote-info">
        <span class="quote-speaker">${escapeHtml(quote.speaker || '?')}</span>
        <span class="quote-importance" style="background: ${importance.color}20; color: ${importance.color}">
          ${importance.label}
        </span>
      </div>

      ${context.situation ? `
        <div class="quote-situation">${escapeHtml(context.situation)}</div>
      ` : ''}

      <div class="quote-meta">
        ${context.turn ? `<span class="quote-turn">턴 ${context.turn}</span>` : ''}
        ${context.worldTime ? `<span class="quote-time">${escapeHtml(context.worldTime)}</span>` : ''}
      </div>

      <div class="memory-item-actions compact">
        <button class="memory-icon-btn" data-action="editMemory" data-layer="quote" data-id="${quote.id}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
        </button>
        <button class="memory-icon-btn danger" data-action="deleteMemory" data-layer="quote" data-id="${quote.id}">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
  `;
}

// ============================================
// Quote 편집 폼 필드
// ============================================

export function getQuoteEditFields(data) {
  const context = data?.context || {};

  return [
    {
      name: 'text',
      label: '대사',
      type: 'textarea',
      value: data?.text || '',
      placeholder: '기억에 남는 대사를 입력하세요',
      required: true
    },
    {
      name: 'speaker',
      label: '화자',
      type: 'text',
      value: data?.speaker || '',
      placeholder: '누가 말했나요?',
      required: true
    },
    {
      name: 'importance',
      label: '중요도',
      type: 'select',
      value: data?.importance || 'medium',
      options: Object.entries(IMPORTANCE_CONFIG).map(([value, config]) => ({
        value,
        label: config.label
      }))
    },
    {
      name: 'situation',
      label: '상황 설명',
      type: 'text',
      value: context.situation || '',
      placeholder: '어떤 상황에서 한 말인가요?'
    }
  ];
}

// ============================================
// Quote 생성 폼 필드
// ============================================

export function getQuoteCreateFields() {
  return [
    {
      name: 'text',
      label: '대사',
      type: 'textarea',
      value: '',
      placeholder: '기억에 남는 대사를 입력하세요',
      required: true
    },
    {
      name: 'speaker',
      label: '화자',
      type: 'text',
      value: '',
      placeholder: '누가 말했나요?',
      required: true
    },
    {
      name: 'importance',
      label: '중요도',
      type: 'select',
      value: 'medium',
      options: Object.entries(IMPORTANCE_CONFIG).map(([value, config]) => ({
        value,
        label: config.label
      }))
    },
    {
      name: 'situation',
      label: '상황 설명',
      type: 'text',
      value: '',
      placeholder: '어떤 상황에서 한 말인가요?'
    }
  ];
}

// ============================================
// Quote 데이터 검증
// ============================================

export function validateQuoteData(formData) {
  const errors = [];

  if (!formData.text?.trim()) {
    errors.push('대사를 입력해주세요');
  }
  if (!formData.speaker?.trim()) {
    errors.push('화자를 입력해주세요');
  }

  return errors;
}

// ============================================
// 폼 데이터 변환
// ============================================

export function transformQuoteFormData(formData) {
  return {
    text: formData.text?.trim(),
    speaker: formData.speaker?.trim(),
    importance: formData.importance || 'medium',
    context: {
      situation: formData.situation?.trim() || ''
    }
  };
}
